import { useState, type JSX } from "react";
import {
  Alert,
  Box,
  Button as MuiButton,
  InputAdornment,
  Tab,
  Tabs,
  TextField,
} from "@mui/material";
import { Button, Typography } from "@platform/design-system";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { AxiosInstance } from "axios";

import {
  Rate,
  RateCategory,
  RateSchedule,
  RateScheduleInput,
} from "../../types";
import {
  coverageTierToLabelLong,
  sortByAgeBandBounds,
  sortRatesByCoverageTier,
} from "../../utils/rateFormatters";
import { slotKeyOf } from "../../utils/rateSets";
import {
  useDeleteRateSchedulesByCategory,
  useReplaceRateSchedulesByCategory,
} from "../../hooks/useRates";

const RATE_SET_TABS: { value: RateCategory; label: string }[] = [
  { value: "current", label: "Current" },
  { value: "renewal", label: "Renewal" },
  { value: "revisedRenewal", label: "Revised Renewal" },
];

const RATE_SET_LABEL: Record<RateCategory, string> = {
  current: "Current",
  renewal: "Renewal",
  revisedRenewal: "Revised Renewal",
};

const SPACE_BETWEEN = "space-between";
const ALIGN_RIGHT = "right";
// Wide enough for a small TextField with its "$" adornment; the table scrolls
// horizontally rather than squeezing below these when a design has many splits.
const RATE_COLUMN_MIN_WIDTH = "110px";
const AGE_BAND_COLUMN_MIN_WIDTH = "72px";
/** Header for the single column of an age-banded design with no tier / gender / tobacco split. */
const RATE_COLUMN_FALLBACK_LABEL = "Monthly Rate";

const rateCategoryOf = (schedule: RateSchedule): RateCategory =>
  (schedule.rateCategory ?? "current") as RateCategory;

const hasAgeBand = (rate: Rate): boolean =>
  rate.ageBandLower != null || rate.ageBandUpper != null;

const ageBandLabel = (rate: Rate): string | undefined => {
  if (!hasAgeBand(rate)) return undefined;

  const lower = rate.ageBandLower ?? 0;

  return rate.ageBandUpper == null
    ? `${lower}+`
    : `${lower}–${rate.ageBandUpper}`;
};

/** Age-band identity, matching the band segment of `slotKeyOf`. */
const ageBandKey = (rate: Rate): string =>
  [
    rate.ageBandLower != null ? `l${rate.ageBandLower}` : null,
    rate.ageBandUpper != null ? `u${rate.ageBandUpper}` : null,
  ]
    .filter(Boolean)
    .join("-");

const capitalize = (value: string): string =>
  value.charAt(0).toUpperCase() + value.slice(1);

/**
 * Identity of the part of a rate other than its age band — its column in an age-banded
 * table. Keyed off the codes rather than the display labels so two tiers can never share
 * a column because their labels happen to read alike.
 */
const rateGroupKey = (rate: Rate): string =>
  [
    rate.coverageTierCode,
    rate.gender,
    rate.isTobaccoRated != null
      ? rate.isTobaccoRated
        ? "tob"
        : "nontob"
      : null,
  ]
    .filter(Boolean)
    .join("-");

/**
 * The part of a rate's identity other than its age band — "Employee Only", "Male",
 * "Tobacco". Empty for a rate with no tier, gender or tobacco split.
 */
const rateGroupLabel = (rate: Rate): string =>
  [
    rate.coverageTierCode
      ? coverageTierToLabelLong(rate.coverageTierCode)
      : undefined,
    rate.gender ? capitalize(String(rate.gender)) : undefined,
    rate.isTobaccoRated != null
      ? rate.isTobaccoRated
        ? "Tobacco"
        : "Non-Tobacco"
      : undefined,
  ]
    .filter(Boolean)
    .join(" · ");

/** Human label for a slot, e.g. "Employee Only", "5–9 · Employee", "10+ · Male". */
const slotLabel = (rate: Rate): string => {
  const label = [ageBandLabel(rate), rateGroupLabel(rate)]
    .filter(Boolean)
    .join(" · ");

  return label.length > 0 ? label : "Rate";
};

/**
 * A rate's entry-slot key, namespaced by section. AD&D rates share a slotKeyOf with the
 * primary section's rates (e.g. a life "Rate per $1,000" and an AD&D "Rate per $1,000" both
 * have no tier and the same unit), so an unnamespaced key would let their draft entries
 * collide. Primary keys stay bare so existing behavior is unchanged.
 */
const slotKeyFor = (schedule: RateSchedule, rate: Rate): string =>
  schedule.rateCoverageIndicator === "adnd"
    ? `adnd:${slotKeyOf(rate)}`
    : slotKeyOf(rate);

/** Slot label within a section; an AD&D rate with no tier is the employee AD&D rate. */
const sectionSlotLabel = (rate: Rate, isAdnd: boolean): string => {
  const label = slotLabel(rate);
  return label === "Rate" && isAdnd ? "Employee" : label;
};

/** A rate slot the analyst reads or fills in. */
interface Slot {
  key: string;
  label: string;
}

interface SlotEntry {
  slot: Slot;
  rate: Rate;
}

/**
 * Age-banded rates laid out as a table: one row per age band, one column per coverage
 * tier (or gender / tobacco split). A cell is undefined when the carrier did not quote
 * that band for that column.
 */
interface AgeBandTable {
  columns: { key: string; label: string }[];
  rows: { key: string; label: string; cells: (Slot | undefined)[] }[];
}

interface Section {
  heading?: string;
  ageBandTable?: AgeBandTable;
  /** Rates with no age band — a companion child rate, a flat AD&D rate — one row each. */
  slots: Slot[];
}

const uniqueBy = <T,>(items: T[], keyOf: (item: T) => string): T[] => [
  ...new Map(items.map((item) => [keyOf(item), item])).values(),
];

/**
 * Age bands as rows and their tier / gender / tobacco splits as columns, so a band's
 * rates read across one line instead of the splits interleaving down a single column.
 * Columns follow the canonical tier order and rows run youngest band first, matching the
 * Build tab grid and the export.
 */
const buildAgeBandTable = (entries: SlotEntry[]): AgeBandTable | undefined => {
  if (entries.length === 0) return undefined;

  const rates = entries.map(({ rate }) => rate);

  const columns = sortRatesByCoverageTier(uniqueBy(rates, rateGroupKey)).map(
    (rate) => ({ key: rateGroupKey(rate), label: rateGroupLabel(rate) }),
  );

  const rows = sortByAgeBandBounds(
    uniqueBy(rates, ageBandKey).map((rate) => ({
      key: ageBandKey(rate),
      label: ageBandLabel(rate) ?? "",
      lower: rate.ageBandLower,
      upper: rate.ageBandUpper,
    })),
  ).map(({ key, label }) => ({
    key,
    label,
    cells: columns.map(
      (column) =>
        entries.find(
          ({ rate }) =>
            ageBandKey(rate) === key && rateGroupKey(rate) === column.key,
        )?.slot,
    ),
  }));

  return { columns, rows };
};

/** Column tracks shared by an age-band table's header and its rows. */
const ageBandGridSx = (columnCount: number) => ({
  display: "grid",
  gridTemplateColumns: `minmax(${AGE_BAND_COLUMN_MIN_WIDTH}, 1fr) repeat(${columnCount}, minmax(${RATE_COLUMN_MIN_WIDTH}, 1fr))`,
  columnGap: 1,
  alignItems: "center",
});

export interface RateSetsPanelProps {
  benefitPlanId: string;
  /** All rate schedules for the plan, across every category. */
  schedules: RateSchedule[];
  axiosInstance: AxiosInstance;
  onSaved?: () => void;
}

/**
 * Current / Renewal / Revised Renewal rate sets for a current plan. Current rates are
 * read-only here (set on the Build tab); renewal and revised renewal rates are entered
 * per rate slot and persisted to their own category, inheriting the current plan's full
 * structure — coverage tiers for tiered designs, and age bands / gender / tobacco
 * splits (plus any companion child/dependent flat schedule) for age-banded designs.
 *
 * Rendered only via RateSetsSection, which guarantees a present plan + axios
 * instance before mounting this panel.
 */
export const RateSetsPanel = ({
  benefitPlanId,
  schedules,
  axiosInstance,
  onSaved,
}: RateSetsPanelProps): JSX.Element => {
  const [activeSet, setActiveSet] = useState<RateCategory>("current");
  // null = show empty state (renewal/revised with no draft); a map = show the value table.
  const [draft, setDraft] = useState<Record<string, string> | null>(null);

  const replace = useReplaceRateSchedulesByCategory(axiosInstance);
  const remove = useDeleteRateSchedulesByCategory(axiosInstance);

  const schedulesFor = (set: RateCategory): RateSchedule[] =>
    schedules.filter((schedule) => rateCategoryOf(schedule) === set);
  const hasData = (set: RateCategory): boolean => schedulesFor(set).length > 0;

  // The current plan's schedules define the structure renewal/revised rates inherit — the
  // primary schedule, any companion child/dependent flat schedule, and any AD&D schedule.
  // Renewal entry reproduces every one of them.
  const currentStructureSchedules = schedulesFor("current");

  // Entry slots grouped into the primary section and the AD&D section (rendered under its
  // own subheading). AD&D is omitted when the plan has no AD&D schedule.
  const entriesForSection = (isAdnd: boolean): SlotEntry[] =>
    currentStructureSchedules
      .filter(
        (schedule) => (schedule.rateCoverageIndicator === "adnd") === isAdnd,
      )
      .flatMap((schedule) =>
        (schedule.rates ?? []).map((rate) => ({
          slot: {
            key: slotKeyFor(schedule, rate),
            label: sectionSlotLabel(rate, isAdnd),
          },
          rate,
        })),
      );

  // Age-banded rates become a band × tier table; the rest stay one row per slot.
  const buildSection = (isAdnd: boolean): Section => {
    const entries = entriesForSection(isAdnd);

    return {
      heading: isAdnd ? "AD&D" : undefined,
      ageBandTable: buildAgeBandTable(
        entries.filter((e) => hasAgeBand(e.rate)),
      ),
      slots: entries
        .filter((entry) => !hasAgeBand(entry.rate))
        .map((entry) => entry.slot),
    };
  };

  const sections: Section[] = [buildSection(false), buildSection(true)].filter(
    (section) => section.ageBandTable !== undefined || section.slots.length > 0,
  );

  const slots: Slot[] = sections.flatMap((section) => [
    ...(section.ageBandTable?.rows ?? []).flatMap((row) =>
      row.cells.filter((cell): cell is Slot => cell !== undefined),
    ),
    ...section.slots,
  ]);

  const valuesForSet = (set: RateCategory): Record<string, string> | null => {
    const sets = schedulesFor(set);
    if (sets.length === 0) return null;

    return Object.fromEntries(
      sets.flatMap((schedule) =>
        (schedule.rates ?? []).map((rate) => [
          slotKeyFor(schedule, rate),
          rate.rate != null ? String(rate.rate) : "",
        ]),
      ),
    );
  };

  const selectSet = (set: RateCategory): void => {
    setActiveSet(set);
    setDraft(set === "current" ? null : valuesForSet(set));
  };

  const startAdding = (): void =>
    setDraft(Object.fromEntries(slots.map((slot) => [slot.key, ""])));

  // Reproduce each current structure schedule for the active category, copying every
  // structural field (and the inherited volume/unit basis) verbatim and overwriting
  // only the rate with the analyst's entry.
  const buildSchedules = (): RateScheduleInput[] =>
    currentStructureSchedules.map((schedule) => ({
      rateDesign: schedule.rateDesign,
      rateCoverageIndicator: schedule.rateCoverageIndicator,
      rates: (schedule.rates ?? []).map((rate) => ({
        coverageTierCode: rate.coverageTierCode,
        ageBandLower: rate.ageBandLower,
        ageBandUpper: rate.ageBandUpper,
        gender: rate.gender,
        isTobaccoRated: rate.isTobaccoRated,
        numberOfLives: rate.numberOfLives,
        volume: rate.volume,
        unit: rate.unit,
        rate: Number(draft?.[slotKeyFor(schedule, rate)]),
      })),
    }));

  const handleSave = async (): Promise<void> => {
    if (!draft || currentStructureSchedules.length === 0) return;
    try {
      await replace.mutateAsync({
        benefitPlanId,
        category: activeSet,
        rateSchedules: buildSchedules(),
      });
      onSaved?.();
    } catch {
      // errors surfaced via useRatesErrorHandler in the mutation hooks
    }
  };

  const handleRemove = async (): Promise<void> => {
    try {
      await remove.mutateAsync({ benefitPlanId, category: activeSet });
      setDraft(null);
      onSaved?.();
    } catch {
      // errors surfaced via useRatesErrorHandler in the mutation hooks
    }
  };

  const currentValues = valuesForSet("current") ?? {};
  const isMutating = replace.isPending || remove.isPending;
  const showValueTable = activeSet !== "current" && draft !== null;
  // Every slot must have a value before saving — a blank field would otherwise
  // persist as a rate the analyst never entered.
  const hasBlankValue = slots.some(
    (slot) => (draft?.[slot.key] ?? "").trim() === "",
  );

  const readOnlyCell = (slot: Slot): JSX.Element => (
    <Typography variant="body-medium" sx={{ textAlign: ALIGN_RIGHT }}>
      {currentValues[slot.key] ? `$${currentValues[slot.key]}` : "—"}
    </Typography>
  );

  const entryCell = (slot: Slot): JSX.Element => (
    <TextField
      data-testid={`rate-sets-tier-${slot.key}`}
      size="small"
      type="number"
      value={draft?.[slot.key] ?? ""}
      onChange={(event) =>
        setDraft((prev) => ({
          ...(prev ?? {}),
          [slot.key]: event.target.value,
        }))
      }
      InputProps={{
        startAdornment: <InputAdornment position="start">$</InputAdornment>,
      }}
      fullWidth
    />
  );

  const headerTextSx = { display: "block", fontWeight: 600 };

  const renderAgeBandTable = (
    table: AgeBandTable,
    renderCell: (slot: Slot) => JSX.Element,
  ): JSX.Element => (
    <Box sx={{ overflowX: "auto" }}>
      <Box
        sx={{
          ...ageBandGridSx(table.columns.length),
          px: 1,
          pb: 1,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Typography variant="caption" sx={headerTextSx}>
          Age Band
        </Typography>
        {table.columns.map((column) => (
          <Typography
            key={column.key}
            data-testid="rate-sets-column-header"
            variant="caption"
            sx={{ ...headerTextSx, textAlign: ALIGN_RIGHT }}
          >
            {column.label || RATE_COLUMN_FALLBACK_LABEL}
          </Typography>
        ))}
      </Box>

      {table.rows.map((row) => (
        <Box
          key={row.key}
          data-testid={`rate-sets-row-${row.key}`}
          sx={{ ...ageBandGridSx(table.columns.length), px: 1, py: 1 }}
        >
          <Typography variant="body-medium">{row.label}</Typography>
          {row.cells.map((cell, index) => (
            <Box key={table.columns[index].key}>{cell && renderCell(cell)}</Box>
          ))}
        </Box>
      ))}
    </Box>
  );

  const renderSlotRows = (
    sectionSlots: Slot[],
    renderCell: (slot: Slot) => JSX.Element,
  ): JSX.Element => (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: SPACE_BETWEEN,
          px: 1,
          pb: 1,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Typography variant="caption" sx={headerTextSx}>
          Coverage Tier
        </Typography>
        <Typography variant="caption" sx={headerTextSx}>
          Monthly Rate
        </Typography>
      </Box>

      {sectionSlots.map((slot) => (
        <Box
          key={slot.key}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: SPACE_BETWEEN,
            px: 1,
            py: 1,
          }}
        >
          <Typography variant="body-medium">{slot.label}</Typography>
          <Box sx={{ width: 160, textAlign: ALIGN_RIGHT }}>
            {renderCell(slot)}
          </Box>
        </Box>
      ))}
    </Box>
  );

  // Render each section (primary, then AD&D under its subheading); `renderCell` supplies the
  // per-slot cell — read-only on the Current tab, an editable field on the renewal tabs.
  const renderSections = (
    renderCell: (slot: Slot) => JSX.Element,
  ): JSX.Element[] =>
    sections.map((section) => (
      <Box key={section.heading ?? "primary"}>
        {section.heading && (
          <Typography
            variant="caption"
            color="textSecondary"
            sx={{ ...headerTextSx, mt: 1 }}
          >
            {section.heading}
          </Typography>
        )}
        {section.ageBandTable &&
          renderAgeBandTable(section.ageBandTable, renderCell)}
        {section.slots.length > 0 && renderSlotRows(section.slots, renderCell)}
      </Box>
    ));

  return (
    <Box
      data-testid="rate-sets-panel"
      sx={{ mt: 3, border: 1, borderColor: "divider", borderRadius: 1, p: 2 }}
    >
      <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1 }}>
        Rate Sets
      </Typography>

      <Tabs
        value={activeSet}
        onChange={(_event, value: RateCategory) => selectSet(value)}
        textColor="secondary"
        indicatorColor="secondary"
        sx={{
          borderBottom: 1,
          borderColor: "divider",
          mb: 2,
          // Gray out the disabled Revised Renewal tab so it reads as unavailable
          // until renewal rates exist (matches the Figma).
          "& .MuiTab-root.Mui-disabled": { color: "text.disabled" },
          "& .MuiTab-root.Mui-selected": { color: "brandPrimary.main" },
          "& .MuiTabs-indicator": { backgroundColor: "brandPrimary.main" },
        }}
      >
        {RATE_SET_TABS.map((tab) => (
          <Tab
            key={tab.value}
            value={tab.value}
            // Revised renewal requires renewal rates to exist first (mirrors the backend gate).
            disabled={tab.value === "revisedRenewal" && !hasData("renewal")}
            iconPosition="start"
            icon={
              hasData(tab.value) ? (
                <CheckCircleIcon color="brandPrimary" fontSize="small" />
              ) : undefined
            }
            label={tab.label}
          />
        ))}
      </Tabs>

      {activeSet === "current" && (
        <Box>
          <Alert severity="info" sx={{ mb: 2 }}>
            The Current Plan rates are set in the Build tab and cannot be edited
            here.
          </Alert>

          {renderSections(readOnlyCell)}
        </Box>
      )}

      {activeSet !== "current" && slots.length === 0 && (
        <Alert severity="info">
          Renewal rate entry requires the current plan to have rates.
        </Alert>
      )}

      {activeSet !== "current" && slots.length > 0 && !showValueTable && (
        <Box sx={{ textAlign: "center", py: 3 }}>
          <AddCircleOutlineIcon color="brandPrimary" />
          <Typography variant="subtitle1" color="textSecondary" sx={{ mt: 1 }}>
            No {RATE_SET_LABEL[activeSet]} Rates Yet
          </Typography>
          <Typography
            variant="body-medium"
            color="textSecondary"
            sx={{ mb: 2 }}
          >
            Add the carrier-proposed rates for next plan year. The rate
            structure inherits from the current plan.
          </Typography>
          <Button
            data-testid="rate-sets-add"
            variant="primary"
            startIcon={<AddCircleOutlineIcon />}
            onClick={startAdding}
          >
            Add {RATE_SET_LABEL[activeSet]} Rates
          </Button>
        </Box>
      )}

      {showValueTable && (
        <Box>
          {renderSections(entryCell)}

          <Box
            sx={{
              display: "flex",
              justifyContent: SPACE_BETWEEN,
              alignItems: "center",
              mt: 2,
            }}
          >
            {/* TODO(ds-button): no DS low-emphasis danger variant yet */}
            <MuiButton
              data-testid="rate-sets-remove"
              variant="text"
              color="error"
              startIcon={<DeleteOutlineIcon />}
              disabled={isMutating || !hasData(activeSet)}
              onClick={handleRemove}
            >
              Remove {RATE_SET_LABEL[activeSet]} Rates
            </MuiButton>
            <Button
              data-testid="rate-sets-save"
              variant="primary"
              disabled={isMutating || hasBlankValue}
              onClick={handleSave}
            >
              Save {RATE_SET_LABEL[activeSet]} Rates
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default RateSetsPanel;
